import React, { useState } from 'react';
import { Icon } from '../Icon/Icon';
import { TiHeader } from './TiTabs';
import type { TiTabId } from './TiTabs';
import { SetSwitch } from '../SettingsShell/controls';
import {
  TC_NUMBERS,
  deleteTcCampaign,
  duplicateTcCampaign,
  reorderTcCampaigns,
  setTcCampaignState,
  tcCampaignStore,
  tcCampaignsHeader,
} from '../../treatment/mockData';
import type { TcCampaign } from '../../treatment/types';

export interface TiCampaignsViewProps {
  onSelectTab?: (tab: TiTabId) => void;
  onOpenSettings?: () => void;
  onNewCampaign?: () => void;
  onReport?: (id: string) => void;
  onEdit?: (id: string) => void;
  onContinue?: (id: string) => void;
  onSeeUncovered?: () => void;
  /** Opens the Calls section filtered to Treatment (spec 31 §7). */
  onOpenCalls?: () => void;
}

/**
 * Campaigns (amendment 27 §4, board `TC1Campaigns`).
 *
 * **The same shape as `/recall`** (spec 22), with TC columns: drag to reorder,
 * the Active switch, Report/Edit/Duplicate/Delete on a live campaign and
 * Continue on a draft. A patient's treatment is in one campaign at a time — the
 * one higher in this list — which is why the order is draggable and stated under
 * the table.
 */
export function TiCampaignsView({
  onSelectTab,
  onOpenSettings,
  onNewCampaign,
  onReport,
  onEdit,
  onContinue,
  onSeeUncovered,
  onOpenCalls,
}: TiCampaignsViewProps) {
  const [, force] = useState(0);
  const rerender = () => { force((n) => n + 1); };
  const rows = tcCampaignStore.campaigns;
  const inCampaign = rows.reduce((n, c) => n + (c.inProgress ?? 0), 0);

  return (
    <div className="cal-fade mx-auto min-h-0 w-full max-w-[1180px] flex-1 overflow-y-auto px-8 pb-16 pt-6">
      <TiHeader
        active="campaigns"
        eyebrow={tcCampaignsHeader().toUpperCase()}
        onSelectTab={onSelectTab}
        onOpenSettings={onOpenSettings}
        onNewCampaign={onNewCampaign}
        onOpenCalls={onOpenCalls}
      />

      {/* tiles */}
      <div className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Tile label="Patients in a campaign" value={String(inCampaign)} sub={`of ${String(TC_NUMBERS.reachable)} reachable`} />
        <Tile label="Booked · last 30 days" value={String(TC_NUMBERS.booked30)} sub={`from ${String(TC_NUMBERS.messaged30)} patients messaged`} />
        <Tile label="Treatment booked" value={TC_NUMBERS.bookedValue} sub="practice fees" />
        <Tile label="Acceptance" value={`${String(TC_NUMBERS.acceptancePct)}%`} sub={`was ${String(TC_NUMBERS.acceptanceWas)}% before ELVA`} />
      </div>

      {/* the list */}
      <div className="mt-4 overflow-hidden rounded-[var(--r-xl)] border border-[var(--border-default)] bg-[var(--surface-card)] shadow-[var(--shadow-card)]">
        <div className="grid grid-cols-[28px_1.8fr_0.7fr_0.6fr_0.9fr_0.9fr_1.4fr] gap-x-3 border-b border-[var(--border-default)] px-4 py-2.5 font-[var(--font-mono)] text-[9.5px] font-bold uppercase tracking-[0.07em] text-[var(--text-muted)]">
          <span /><span>Campaign</span><span>State</span><span className="text-right">In progress</span><span>Messages</span><span>Last 30 days</span><span className="text-right">Actions</span>
        </div>
        {rows.map((c, i) => (
          <CampaignRow
            key={c.id}
            c={c}
            onToggle={(on) => { setTcCampaignState(c.id, on ? 'active' : 'paused'); rerender(); }}
            onMove={(dir) => {
              const to = i + dir;
              if (to >= 0 && to < rows.length) { reorderTcCampaigns(i, to); rerender(); }
            }}
            onReport={() => { onReport?.(c.id); }}
            onEdit={() => { onEdit?.(c.id); }}
            onContinue={() => { onContinue?.(c.id); }}
            onDuplicate={() => { duplicateTcCampaign(c.id); rerender(); }}
            onDelete={() => { deleteTcCampaign(c.id); rerender(); }}
          />
        ))}
      </div>

      <p className="m-0 mt-3 text-[12px] leading-[1.6] text-[var(--text-muted)]">
        <b className="font-semibold text-[var(--text-secondary)]">A patient is in one campaign at a time</b>{' '}
        — the one higher in this list. Drag to change the order.{' '}
        {TC_NUMBERS.reachable - inCampaign} reachable patients aren’t in any campaign.{' '}
        <button type="button" onClick={onSeeUncovered} className="font-semibold text-[var(--brand-indigo)] hover:underline">
          See who
        </button>
      </p>
    </div>
  );
}

function Tile({ label, value, sub }: { label: string; value: string; sub: string }) {
  return (
    <div className="rounded-[var(--r-xl)] border border-[var(--border-default)] bg-[var(--surface-card)] p-3.5 shadow-[var(--shadow-card)]">
      <div className="text-[10.5px] font-bold uppercase tracking-[0.09em] text-[var(--text-muted)]">{label}</div>
      <div className="mt-1 font-[var(--font-display)] text-[22px] font-semibold tracking-[-0.03em] text-[var(--text-primary)]">{value}</div>
      <div className="mt-0.5 text-[11px] text-[var(--text-muted)]">{sub}</div>
    </div>
  );
}

function CampaignRow({
  c,
  onToggle,
  onMove,
  onReport,
  onEdit,
  onContinue,
  onDuplicate,
  onDelete,
}: {
  c: TcCampaign;
  onToggle: (on: boolean) => void;
  onMove: (dir: -1 | 1) => void;
  onReport: () => void;
  onEdit: () => void;
  onContinue: () => void;
  onDuplicate: () => void;
  onDelete: () => void;
}) {
  const draft = c.state === 'draft';
  const hasSteps = c.steps > 0;
  const [confirming, setConfirming] = useState(false);
  return (
    <div className="grid grid-cols-[28px_1.8fr_0.7fr_0.6fr_0.9fr_0.9fr_1.4fr] items-center gap-x-3 border-b border-[var(--border-default)] px-4 py-3 text-[12.5px] last:border-0">
      <span className="flex flex-col text-[var(--text-muted)]">
        <button type="button" aria-label="Move up" onClick={() => { onMove(-1); }} className="hover:text-[var(--brand-indigo)]"><Icon kind="up" size={11} stroke={2.2} /></button>
        <button type="button" aria-label="Move down" onClick={() => { onMove(1); }} className="hover:text-[var(--brand-indigo)]"><Icon kind="down" size={11} stroke={2.2} /></button>
      </span>
      <span className="min-w-0">
        <span className="block font-semibold text-[var(--text-primary)]">{c.name}</span>
        <span className="block truncate text-[11px] text-[var(--text-muted)]">{c.description}</span>
      </span>
      <span>
        {draft ? (
          <span className="rounded-[5px] bg-[#F1F3F9] px-1.5 py-[1px] text-[9.5px] font-bold uppercase tracking-[0.06em] text-[var(--text-muted)]">Draft</span>
        ) : (
          <span className="inline-flex items-center gap-1.5">
            <SetSwitch label={`${c.name} active`} checked={c.state === 'active'} onChange={onToggle} />
            {c.state === 'paused' ? <span className="text-[10.5px] font-semibold text-[var(--text-muted)]">Paused</span> : null}
          </span>
        )}
      </span>
      <span className="text-right font-[var(--font-mono)] text-[var(--text-secondary)]">{draft ? '—' : (c.inProgress ?? '—')}</span>
      <span className="text-[11.5px] text-[var(--text-secondary)]">
        {hasSteps ? `${String(c.steps)} steps` : 'No steps yet'}
        {hasSteps ? <span className="block text-[10.5px] text-[var(--text-muted)]">{c.channels.join(' · ')}</span> : null}
      </span>
      <span className="text-[11.5px]">
        {draft ? '—' : (
          <>
            <span className="font-semibold text-[var(--text-primary)]">{c.booked30} booked</span>
            <span className="block text-[10.5px] font-[var(--font-mono)] text-[var(--money-green)]">{c.value30}</span>
          </>
        )}
      </span>
      <span className="flex flex-wrap items-center justify-end gap-x-2 gap-y-0.5 text-[11.5px] font-semibold text-[var(--brand-indigo)]">
        {confirming ? (
          <>
            <span className="text-[11px] font-normal text-[var(--text-muted)]">Delete this campaign?</span>
            <button type="button" onClick={() => { setConfirming(false); onDelete(); }} className="text-[var(--alert-red)] hover:underline">Delete</button>
            <button type="button" onClick={() => { setConfirming(false); }} className="hover:underline">Keep</button>
          </>
        ) : (
          <>
            {draft ? (
              <button type="button" onClick={onContinue} className="hover:underline">Continue</button>
            ) : (
              <>
                <button type="button" onClick={onReport} className="hover:underline">Report</button>
                <button type="button" onClick={onEdit} className="hover:underline">Edit</button>
              </>
            )}
            <button type="button" onClick={onDuplicate} className="hover:underline">Duplicate</button>
            <button type="button" onClick={() => { setConfirming(true); }} className="text-[var(--alert-red)] hover:underline">Delete</button>
          </>
        )}
      </span>
    </div>
  );
}
